<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { TrackingBaseData } from '@/api/tracking';
import { DgrToggleButton } from '@stockmarkteam/donguri-ui';
import UserTable from '@/components-capability/admin/themes/user-table.vue';
import HeaderDetailContent from '@/components/common/header/header-detail-content.vue';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import AdminContent from '@/components/layouts/admin-content.vue';
import Header from '@/components/layouts/header.vue';
import AddUsersModal, {
  AddableUser,
} from '@/components/modals/add-users-modal.vue';
import ThemeTitleContent from '@/components/theme/theme-title-content.vue';
import { ThemeUser, UserInfo } from '@/types';
import { formatDate, formatUserName } from '@/utils/formatters';
import {
  STATES,
  useGroups,
  useTeamUsersWithAddUserOptions,
  useThemeList,
  useThemeUsers,
} from '@/utils/swr';
import { getThemeHeaderDetail } from '@/utils/theme';
import { checkMoveToAdmin } from '@/utils/user';
import { useStore } from '@/utils/vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { createSnackbar } = useSnackbar();

const feedType = computed(() => store.state.feedType.feedType);
const { data: themes } = useThemeList(feedType);
const { data: groups } = useGroups();
const { data: teamUserList, state: teamUsersState } =
  useTeamUsersWithAddUserOptions();
const isOpenAddUsersModal = ref(false);
const isOpenAddFavoriteUsersModal = ref(false);

const isAddThemeAndFavorite = ref(true);

const themeId = Number(route.params.themeId);

const theme = computed(() => themes.value?.themes.find(t => t.id === themeId));

const trackingData: TrackingBaseData = {
  pageName: 'theme_users_admin',
  pageUrl: route.fullPath,
};

const fields = [
  {
    name: 'is_favorite',
    displayName: 'お気に入り',
    type: 'boolean',
  },
  {
    name: 'role',
    displayName: '権限',
    type: 'role',
  },
];
const {
  data: themeUsers,
  mutate,
  state: themeUsersState,
} = useThemeUsers(themeId);

const query = ref('');
const filteredThemeUsers = computed(() =>
  (themeUsers.value?.theme_users ?? []).filter(user =>
    [user.email, user.user_name]
      .join()
      .toLowerCase()
      .includes(query.value.toLowerCase()),
  ),
);

onMounted(() => checkMoveToAdmin(router));

const isModalLoading = computed(
  () =>
    themeUsersState.value === STATES.PENDING ||
    themeUsersState.value === STATES.VALIDATING ||
    teamUsersState.value === STATES.PENDING ||
    teamUsersState.value === STATES.VALIDATING,
);

const addableUserList = computed<AddableUser[]>(() => {
  const alreadyAddedUserIds =
    themeUsers.value?.theme_users.map(user => user.id) ?? [];
  return (
    teamUserList.value?.users.filter(teamUser => {
      return !alreadyAddedUserIds.includes(teamUser.id);
    }) ?? []
  );
});

const addableFavoriteThemeUsers = computed<AddableUser[]>(() => {
  const notFavoriteUserIds = (themeUsers.value?.theme_users ?? [])
    .filter(u => !u.is_favorite)
    .map(u => u.id);
  return (
    teamUserList.value?.users.filter(u => notFavoriteUserIds.includes(u.id)) ??
    []
  );
});

const isShowAddUsersModalContractFilter = computed(() => {
  return (
    teamUserList.value?.users.some(
      user => user.managed_contract_name_by_current_user !== '',
    ) ?? false
  );
});

const addUsers = async (userIds: number[]) => {
  const isAddFavorite = isAddThemeAndFavorite.value;
  if (isAddFavorite) {
    // お気に入りAPIはメンバー追加も行うので `createThemeInvitation` を呼ばない
    await api.createThemeFavorites(themeId, userIds, trackingData);
  } else {
    await api.createThemeInvitation(themeId, userIds, trackingData);
  }
  createSnackbar({
    message: `選択したメンバーをテーマに招待しました${
      isAddFavorite ? '(お気に入り登録済み)' : ''
    }`,
    type: 'success',
  });
  mutate();
  isOpenAddUsersModal.value = false;
};

const addFavoriteUsers = async (userIds: number[]) => {
  await api.createThemeFavorites(themeId, userIds, trackingData);
  createSnackbar({
    message: '選択したメンバーのお気に入り登録が完了しました',
    type: 'success',
  });
  mutate();
  isOpenAddFavoriteUsersModal.value = false;
};

const removeFavorite = async (user: UserInfo | ThemeUser) => {
  await api.deleteThemeFavorites(themeId, [user.id], trackingData);
  createSnackbar({
    message: 'お気に入りを解除しました',
    type: 'success',
  });
  mutate();
};

const removeMember = async (user: UserInfo | ThemeUser) => {
  await api.deleteThemeInvitation(themeId, [user.id], trackingData);
  createSnackbar({
    message: 'メンバーを解除しました',
    type: 'success',
  });
  mutate();
};

const userActions = [
  {
    name: 'お気に入り解除',
    func: removeFavorite,
    enabled: (user: UserInfo | ThemeUser) =>
      (user as ThemeUser).is_favorite ?? false,
  },
  {
    name: 'メンバー解除',
    func: removeMember,
    enabled: (_: UserInfo | ThemeUser) => true,
  },
];

const isPersonalTheme = computed(
  () => theme.value?.access_scope === 'personal',
);

const detail = computed(() =>
  theme.value && groups.value?.groups && teamUserList.value?.users
    ? getThemeHeaderDetail(
        theme.value,
        groups?.value.groups,
        teamUserList.value?.users,
        true,
      )
    : { firstLine: '', secondLine: '' },
);

/**
 * getThemeHeaderDetail は項目を「・」でつないだ1つの文字列を返すが、
 * デザインでは項目の間を空けて並べるので、同じ内容を項目ごとに組み立てる。
 */
const detailItems = computed(() => {
  const t = theme.value;
  if (!t?.access_scope) return [];
  const scopeLabel =
    t.access_scope === 'group'
      ? groups.value?.groups.find(g => g.id === t.group_id)?.name
      : t.access_scope === 'personal'
        ? '個人'
        : '全体';
  const creator = teamUserList.value?.users.find(u => u.id === t.creator_id);
  return [
    scopeLabel ? `公開範囲：${scopeLabel}` : '',
    `作成日：${formatDate(t.created_at)}`,
    t.creator_id && creator
      ? `作成者：${formatUserName(creator.user_name, creator.is_deleted, Infinity)}`
      : '',
  ].filter(item => item !== '');
});
</script>

<template>
  <div
    class="theme-memeber-admin"
    v-if="theme && groups && teamUserList && themeUsers"
  >
    <Header
      :title="`${theme.name}`"
      :detail="detail.firstLine"
      icon="hashtag"
      header-width="100%"
    >
      <template #titleContent>
        <ThemeTitleContent :title="`${theme.name}`" left-icon="hashtag" />
      </template>
      <template #detail-content>
        <HeaderDetailContent :is-responsive="true">
          <template #detail>
            <span class="detail-items">
              <span v-for="item in detailItems" :key="item">{{ item }}</span>
            </span>
            <span class="settings-link">
              <router-link
                :to="{ name: 'themeEdit', params: { themeId: theme.id } }"
                >編集</router-link
              >
            </span>
          </template></HeaderDetailContent
        >
        <HeaderDetailContent
          :detail="detail.secondLine"
          :is-responsive="true"
        />
      </template>
    </Header>
    <AdminContent>
      <div class="content">
        <div class="members">
          <div class="header">
            <div class="member-count">
              <span class="c-title-medium--m">メンバー</span>
              <span class="favorite-count c-text-regular--s"
                >{{ themeUsers.theme_users.length }}人(お気に入り:
                {{
                  themeUsers.theme_users.filter(u => u.is_favorite).length
                }}人)</span
              >
            </div>
            <div v-if="!isPersonalTheme" class="action-buttons">
              <div class="add-favorite-button">
                <button
                  class="favorite-button c-outlineBtn c-btn--small c-btn--auto c-text-regular--m"
                  @click="isOpenAddFavoriteUsersModal = true"
                >
                  お気に入り追加
                </button>
              </div>
              <div class="add-member-button">
                <button
                  class="m-create-button c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary c-text-regular--m"
                  @click="isOpenAddUsersModal = true"
                >
                  メンバー追加
                </button>
              </div>
            </div>
          </div>
          <SearchBar v-model="query" placeholder="検索" />
          <div class="spacing-16"></div>
          <div>
            <UserTable
              class="table"
              :fields="fields"
              :data="filteredThemeUsers"
              :actions="userActions"
            ></UserTable>
          </div>
        </div>
      </div>
    </AdminContent>
    <AddUsersModal
      v-model:open="isOpenAddUsersModal"
      :joinable-users="addableUserList"
      :is-use-contract-option="isShowAddUsersModalContractFilter"
      :is-loading="isModalLoading"
      @on-submit="addUsers"
    >
      <template #toggle>
        <DgrToggleButton
          v-model="isAddThemeAndFavorite"
          class="c-text-regular--s"
        >
          お気に入り登録する
        </DgrToggleButton>
      </template>
    </AddUsersModal>
    <AddUsersModal
      title="お気に入り登録するメンバーを追加"
      subtitle="お気に入り登録していないメンバー"
      no-users-message="お気に入り登録できるメンバーはいません。"
      :disable-filters="true"
      v-model:open="isOpenAddFavoriteUsersModal"
      :joinable-users="addableFavoriteThemeUsers"
      :is-loading="isModalLoading"
      @on-submit="addFavoriteUsers"
    />
  </div>
</template>

<style lang="scss" scoped>
// 共通部品（AdminContent）は変えず、この画面からだけデザインの寸法に上書きする
.theme-memeber-admin {
  width: 100%;

  :deep(.content-body) {
    margin: 24px;
  }
}
.detail-items {
  display: inline-flex;
  gap: 8px;
}
.settings-link {
  a {
    margin-left: 0.5em;
    color: $color-green600;
    &:hover {
      border-bottom: solid 1px;
    }
  }
}
.content {
  .action-buttons {
    display: flex;
    gap: 8px;
  }

  .favorite-button {
    background: #fff;
    border-color: $color-green600;
    color: $color-green600;
  }

  .members {
    width: 100%;
    display: flex;
    flex-direction: column;

    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 16px;

      .member-count {
        display: flex;
        flex-direction: column;
      }

      .favorite-count {
        color: $color-gray600;
      }
    }

    .table {
      width: 100%;
    }
  }
}
</style>
